import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";

export type PreviewFile = {
  id?: string;
  url: string;
  name: string;
  type?: string;
  /** Bytes; shown in the preview header when known. */
  size?: number;
};

type PreviewOptions = {
  swipe?: boolean;
  pinchZoom?: boolean;
  momentum?: boolean;
};

type PreviewState = {
  files: PreviewFile[];
  index: number;
  open: boolean;
  options?: PreviewOptions;
};

type PreviewContextType = {
  state: PreviewState;
  openPreview: (files: PreviewFile[], startIndex?: number) => void;
  closePreview: () => void;
  next: () => void;
  prev: () => void;
  setIndex: (index: number) => void;
};

const PreviewContext = createContext<PreviewContextType | null>(null);

export function PreviewProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PreviewState>({
    files: [],
    index: 0,
    open: false,
  });

  const openPreview = (
    files: PreviewFile[],
    startIndex = 0,
    options?: PreviewOptions,
  ) => {
    setState({ files, index: startIndex, open: true, options });
  };

  const closePreview = () => setState((s) => ({ ...s, open: false }));

  const next = () =>
    setState((s) => ({
      ...s,
      index: (s.index + 1) % s.files.length,
    }));

  const prev = () =>
    setState((s) => ({
      ...s,
      index: s.index === 0 ? s.files.length - 1 : s.index - 1,
    }));

  const setIndex = (index: number) =>
    setState((s) => ({
      ...s,
      index,
    }));

  return (
    <PreviewContext.Provider
      value={{ state, openPreview, closePreview, next, prev, setIndex }}
    >
      {children}
    </PreviewContext.Provider>
  );
}

export const usePreview = () => {
  const ctx = useContext(PreviewContext);
  if (!ctx) throw new Error("usePreview must be inside PreviewProvider");
  return ctx;
};
